import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { OrderMeal, OrderWithContext, Restaurant, NonFoodItem, CachedRecentOrFavoriteOrders, RecentOrFavoriteOrder } from '@/models';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';

@Component({
  name: 'CmgOrderConfirmationOrderSummary',
})
export default class CmgOrderConfirmationOrderSummary extends Vue {
  showExpandedContent = false;

  @Prop({ required: true, type: Object })
  order!: OrderWithContext | null;

  @Prop({ required: false, type: Boolean, default: false })
  paidInStore!: boolean;

  @Prop({ required: true, type: Object })
  restaurant!: Restaurant;

  currentFavoriteOrders: CachedRecentOrFavoriteOrders = this.$appStore.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];

  get favoriteOrderIconUrl(): string {
    const isFavorite = this.currentFavoriteOrders.orders.find(o => o.orderId == this.order?.order.orderId);
    const icons = this.$appStore.state.cms.images.icons;

    return isFavorite ? icons.heartFilled.default : icons.heartEmpty.default;
  }

  get isDelivery(): boolean {
    return this.$services.order.isDelivery();
  }

  get meals(): OrderMeal[] {
    return (this.order && this.order.order.meals) || [];
  }

  get loyaltyMedallionImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionReward.default;
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
  }

  get isGuest(): boolean {
    return this.$services.customer.isLoggedIn() !== true;
  }

  get hasDonation(): boolean {
    return !this.order ? false : !!this.$appStore.state.order.charitableCampaign && !!this.order.order.orderDonationAmount;
  }

  get isEligibleForLoyalty(): boolean {
    return this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile)
  }

  get nonFoodItems(): NonFoodItem[] {
    let nonFoodItems: NonFoodItem[] = [];

    if (this.order) {
      nonFoodItems = this.order.order.nonFoodItems || [];
    }

    return nonFoodItems;
  }

  get pointsOnTheWayText(): string {
    return `${this.joinedLoyaltyOnOrderConfirmationPage ? 'WELCOME TO REWARDS.' : ''} POINTS ARE ON THE WAY.`;
  }

  get joinedLoyaltyOnOrderConfirmationPage(): boolean {
    return this.$appStore.state.customer.loyalty.joinedLoyaltyOnOrderConfirmationPage;
  }

  get favoriteIconUrl(): string {
    return this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  get favorites(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];
  }

  get isFavorite(): boolean {
    return !!this.favorites.find(o => o.orderId == this.order?.order.orderId);
  }

  get favoriteOrderName(): string | undefined {
    return this.favorites.find(o => o.orderId == this.order?.order.orderId)?.favoriteName;
  }

  get recents(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get isLoggedIn(): boolean {
    return this.$services.customer.isLoggedIn() === true;
  }

  get isCrewTippingEnabled(): boolean | undefined {
    return this.restaurant.crewTipPickupEnabled && this.$appStore.state.featureFlags.orderingStatus?.isCrewTippingEnabled;
  }

  async mounted() {
    await this.refreshFavorites();
  }

  async refreshFavorites() {
    this.$services.logger.debug('Refreshing favorites');

    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    }
  }

  formatPrice(price: number | undefined): string {
    return this.$services.utils.formatPrice(price);
  }

  onClickToggleExpandedContentVisibility() {
    this.showExpandedContent = !this.showExpandedContent;
  }

  async onClickFavorite(event: any): Promise<void> {
    this.$services.recentAndFavoriteOrders.clearRecentOrdersCache();
    await this.$services.recentAndFavoriteOrders.refreshRecentOrders();

    const recentOrderToFav = this.recents.find(r => r.orderId === this.order?.order.orderId);

    if (recentOrderToFav) {
      this.$services.ui.setModalVisibility('addFavoriteOrder', true, { order: recentOrderToFav });
      this.$services.ui.firstFocus();
    }
  }
}
